import { Component, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { ToastController, LoadingController, AlertController } from '@ionic/angular';
import { BaseClass } from 'src/app/shared/classes/base';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { Note } from '../_interfaces/note.interface';
import { NotepadService } from '../notepad.service';
import { UserItem } from 'src/app/shared/services/auth.service';
import { SubordinateNotes } from '../_interfaces/subordinate-notes.interface';
import { Subject } from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';

@Component({
  selector: 'app-subordinates-notes',
  templateUrl: './subordinates-notes.component.html',
  styleUrls: ['../notepad.page.scss']
})
export class SubordinatesNotesComponent extends BaseClass implements OnDestroy {

  @Input() currentUser: UserItem;
  @Input() isAll: boolean;
  @Input() set setSubordinateNotes(subordinateNotes: Array<SubordinateNotes>) {
    this.originalUsers = JSON.parse(JSON.stringify(subordinateNotes));
    this.displayedUsers = JSON.parse(JSON.stringify(subordinateNotes));
  }

  @Output() reloadNotes = new EventEmitter();
  @Output() userSelected: EventEmitter<SubordinateNotes> = new EventEmitter<SubordinateNotes>();

  public displayedUsers: Array<SubordinateNotes>;
  public originalUsers: Array<SubordinateNotes>;
  public currentSegment: string = 'user';
  public currentNote: Note = new Note('');
  public originalNote: Note = new Note('');
  public selectedUser: SubordinateNotes;
  public selectedNotesUser: number = 0;

  private _searchSubject: Subject<string> = new Subject();

  constructor(
    protected _toastController: ToastController,
    protected _translationService: TranslationService,
    private _loadingController: LoadingController,
    private _alertController: AlertController,
    private _notepadService: NotepadService
  ) {
    super(_toastController, _translationService);
    this._setSearchSubscription();
  }

  ngOnInit() {
    this.displayedUsers = this.originalUsers.slice(0, 20);
    if (this.selectedUser)
      this.selectUser(this.selectedUser);
  }

  showMoreUsers() {
    var actualCount = this.displayedUsers.length;
    this.displayedUsers = this.originalUsers.slice(0, actualCount + 20);
  }

  showMoreNotes() {
    var actualNotesCount = this.selectedUser.notes ? this.selectedUser.notes.length : 0;
    var userIndex = this.originalUsers.findIndex((user) => { return (user.id === this.selectedUser.id) });

    this.selectedUser.notes = this.originalUsers[userIndex].notes.slice(0, actualNotesCount + 5);
  }


  public setNewNote(): void {
    this.currentNote = new Note('');
  }

  public cancelEdit(userId: string, noteId: number): void {
    this.setNewNote();
    var userIndex = this.displayedUsers.findIndex((user) => user.id === userId);
    var noteIndex = this.displayedUsers[userIndex].notes.findIndex((note) => note.id === noteId);
    var originalText = this.originalUsers[userIndex].notes[noteIndex].text;
    this.displayedUsers[userIndex].notes[noteIndex].text = originalText;
  }

  public editNote(note: Note): void {
    this.currentNote = note;
    this.originalNote = note;
  }

  public updateSearch(searchTextValue: string): void {
    this._searchSubject.next(searchTextValue);
  }

  public selectUser(user: SubordinateNotes): void {
    this.setNewNote();
    var userIndex = this.originalUsers.findIndex((users) => { return (users.id === user.id) });
    this.selectedNotesUser = JSON.parse(JSON.stringify(this.originalUsers))[userIndex].notes.length;
    var userAux = JSON.parse(JSON.stringify(this.originalUsers))[userIndex];
    userAux.notes = userAux.notes.splice(0, 5);

    this.selectedUser = JSON.parse(JSON.stringify(userAux));
  }

  public saveNote(note: Note) {
    note.id ?
      this._updateNote(note) :
      this._addNote(note.text, this.selectedUser.id);
  }

  public updateSelectedUser(): void {
    setTimeout(() => {
      if (this.selectedUser) {
        this.selectedUser = this.originalUsers.find(
          u => u.id === this.selectedUser.id
        );
        this.ngOnInit();
      }
    });
  }

  private async _addNote(text: string, userId: string): Promise<void> {
    const loading = await this._loadingController.create();
    await loading.present();

    this._notepadService.addSubordinateNote(
      text, userId, this.isAll
    ).subscribe(() => {
      this.currentNote = new Note('');
      loading.dismiss();

      this.notifySuccess();
      this.reloadNotes.emit();
      this.ngOnInit();

    }, (error) => {
      loading.dismiss();
      this.notifyError(error);
    });
  }

  private async _updateNote(note: Note): Promise<void> {
    const loading = await this._loadingController.create();
    await loading.present();

    this._notepadService.updateNote(
      note
    ).subscribe(async () => {
      this.currentNote = new Note('');
      loading.dismiss();

      this.notifySuccess();
      this.reloadNotes.emit();
      this.ngOnInit();

    }, (error) => {
      loading.dismiss();
      this.notifyError(error);
    });
  }

  public async confirmDeleteNote(noteId: number): Promise<void> {
    const alert = await this._alertController.create({
      message: this._translationService.getTranslation('notepad.confirmDeleteNote'),
      buttons: [{
        text: this._translationService.getTranslation('generic.no'),
        role: 'cancel',
        cssClass: 'danger'
      }, {
        text: this._translationService.getTranslation('generic.yes'),
        handler: () => {
          this._deleteNote(noteId);
        }
      }]
    });

    await alert.present();
  }

  private _deleteNote(noteId: number): void {
    this._notepadService.deleteNote(
      noteId
    ).subscribe(() => {
      this.notifySuccess();
      this.reloadNotes.emit();

    }, (error) => this.notifyError(error));
  }

  private _setSearchSubscription() {
    this._searchSubject.pipe(
        distinctUntilChanged(),
        debounceTime(1000)
    ).subscribe((searchValue: string) => {
      if (searchValue) {
        this.displayedUsers = this.originalUsers.filter(user => {
          return user.name.toLowerCase().includes(
            searchValue.toLowerCase().trim()
          );
        }).splice(0, 20);
      } else {
        this.displayedUsers = this.originalUsers.splice(0, 20);
      }
    });
  }

  ngOnDestroy() {
    this._searchSubject.unsubscribe();
  }

}
